import styled, { css } from 'styled-components';
import {
  BORDER_RADIUS,
  BUTTON_VARIANTS,
  COLOURS,
  FOCUS_RING_WIDTH,
  FONT_WEIGHT,
  getBodyFont,
  isFocused,
  mediaMap,
  spacingMixin
} from '@belong/themes';
import { ButtonVariantValue, IButton, MediaMapResult, ObjectValues, StyledCSS } from '@belong/types';
import { LinearProgress } from '@belong/ui-core';
import { Icon } from '../../Icon/Icon';
import { ButtonUnstyled } from '../ButtonUnstyled';

const borderWidth = 0.2; // rems
const PADDING_SIZE = {
  // rems
  SMALL: {
    HORZ: 1.4,
    VERT: 0.6
  },
  DEFAULT: {
    HORZ: 2.2,
    VERT: 1
  }
};

export const iconOnlyButtonStyles = css`
  && {
    padding: 1rem;
  }

  border-radius: 100%;
`;

export const iconOnlyLabelStyles = css`
  position: absolute;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  width: 1px;
  height: 1px;
  margin: -1px;
  border: 0;
  overflow: hidden;
  white-space: nowrap;
`;

export const iconOnlyIconStyles = css`
  width: 2.4rem;
  height: 2.4rem;
  margin-right: 0;
  margin-left: 0;
`;

export const BaseStyles = css<any>`
  ${spacingMixin}

  cursor: pointer;
  ${({ disabled, hasAriaDisabledState }) =>
    (disabled || hasAriaDisabledState) &&
    css`
      cursor: not-allowed;
    `}
  text-align: center;
  border-width: ${borderWidth}rem;
  border-style: solid;
  max-width: 100%;
  color: ${COLOURS.BLACK};
  position: relative;
  // To hide scroll bars when button loader running
  // Firefox
  scrollbar-width: none;
  // Everyone else
  &::-webkit-scrollbar {
    display: none;
  }

  ${({ disabled, hasAriaDisabledState }) =>
    !(disabled || hasAriaDisabledState) &&
    css`
      &:focus,
      &:active {
        ${(): StyledCSS => isFocused()};
      }
    `}

  // Quat buttons get the regular rectangular focus outline.
  // Other button types have the custom <FocusRing> instead.
  &:focus,
  &:active {
    ${({ variant }) =>
      variant !== BUTTON_VARIANTS.QUATERNARY
        ? css`
            outline: none;
          `
        : ''};
  }
`;

const focusRingOffset = `${FOCUS_RING_WIDTH * 3}rem`;

export const BaseFontStyles = css`
  font-weight: ${FONT_WEIGHT.SEMI_BOLD};
  ${getBodyFont('medium')}
`;

export const LoadingAnimation = styled(LinearProgress)<{ isLightColor?: boolean }>`
  position: absolute;
  left: -${borderWidth}rem;
  top: -${borderWidth}rem;
  width: calc(100% + ${borderWidth * 2}rem);
  height: calc(100% + ${borderWidth * 2}rem);

  border-radius: ${BORDER_RADIUS.BUTTON}; // firefox needs this

  ${({ isLightColor }) => css`
    background-color: ${({ theme }) =>
      isLightColor
        ? theme?.button?.primary?.isLightColor?.loading?.backgroundColor
        : theme?.button?.primary.loading?.backgroundColor};
  `}
`;

export const ContentContainer = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ChildrenContainer = styled.span`
  &.iconOnly {
    ${iconOnlyLabelStyles}
  }
`;

const SizeModifier = ({ isSmall }: IButton): StyledCSS<IButton> => css`
  padding: ${isSmall
    ? `${PADDING_SIZE.SMALL.VERT}rem ${PADDING_SIZE.SMALL.HORZ}rem`
    : `${PADDING_SIZE.DEFAULT.VERT}rem ${PADDING_SIZE.DEFAULT.HORZ}rem`};
`;

const WidthModifier = ({ width, iconOnly }: IButton): MediaMapResult =>
  mediaMap(width, (result: any) => {
    if (!iconOnly) {
      switch (result) {
        case 'full':
          return css`
            min-width: 100%;
          `;
        case 'half':
          return css`
            min-width: calc(50% - 0.8rem);
          `;
        case 'min':
          return css`
            min-width: 12rem;
          `;
        case 'default':
        default:
          return css`
            min-width: 0;
          `;
      }
    }

    return css`
      min-width: 0;
    `;
  });

const PrimaryStyles = ({
  hasAriaDisabledState,
  isLightColor,
  isLoading,
  selected,
  iconOnly
}: IButton): StyledCSS<IButton> => css<IButton>`
  background-color: ${({ theme }) => theme?.button?.primary.backgroundColor};
  border-color: ${({ theme }) => theme?.button?.primary.borderColor};
  color: ${({ theme }) => theme?.button?.primary.color};
  transition:
    0.2s background-color ease,
    0.2s border-color ease;
  border-radius: ${BORDER_RADIUS.BUTTON};

  ${isLightColor &&
  css`
    background-color: ${({ theme }) => theme?.button?.primary.isLightColor?.backgroundColor};
    color: ${({ theme }) => theme?.button?.primary.isLightColor?.color};

    ${!isLoading &&
    css`
      &:hover {
        background-color: ${({ theme }) => theme?.button?.primary.isLightColor?.hover?.backgroundColor};
        border-color: ${({ theme }) => theme?.button?.primary.isLightColor?.hover?.borderColor};
        color: ${({ theme }) => theme?.button?.primary.isLightColor?.hover?.color};
      }
    `}

    &:active {
      background-color: ${({ theme }) => theme?.button?.primary.isLightColor?.active?.backgroundColor};
      color: ${({ theme }) => theme?.button?.primary.isLightColor?.active?.color};
    }
  `}

  ${isLoading &&
  css`
    cursor: not-allowed;
  `}

  ${!isLoading &&
  !isLightColor &&
  css`
    &:hover {
      background-color: ${({ theme }) => theme?.button?.primary.hover?.backgroundColor};
      border-color: ${({ theme }) => theme?.button?.primary.hover?.borderColor};
      color: ${isLightColor ? COLOURS.WHITE : ({ theme }) => theme?.button?.primary.hover?.color};
    }

    &:active {
      background-color: ${({ theme }) => theme?.button?.primary.active?.backgroundColor};
      border-color: ${({ theme }) => theme?.button?.primary.active?.borderColor};
    }
  `}

${hasAriaDisabledState &&
  !isLoading &&
  css`
    &,
    &:hover {
      background-color: ${({ theme }) => theme?.button?.primary.disabled.backgroundColor};
      border-color: ${({ theme }) =>
        isLoading ? theme?.button?.primary.loading?.borderColor : theme?.button?.primary.disabled?.borderColor};
      ${!isLoading &&
      css`
        color: ${({ theme }) => theme?.button?.primary.disabled.color};
      `}
    }
  `}

  ${SizeModifier};
  ${WidthModifier};

  ${selected &&
  css`
    cursor: default;
    background: ${({ theme }) => theme?.button?.primary.selected?.background};
    border: ${({ theme }) => theme?.button?.primary.selected?.border};

    &:hover {
      background-color: ${({ theme }) => theme?.button?.primary.selected?.hover.backgroundColor};
      border: ${({ theme }) => theme?.button?.primary.selected?.hover.border};
    }
    &:active {
      background-color: ${({ theme }) => theme?.button?.primary.selected?.active.backgroundColor};
      border: ${({ theme }) => theme?.button?.primary.selected?.active.border};
    }
  `}

  ${iconOnly &&
  css`
    ${iconOnlyButtonStyles}
  `}
`;

const SecondaryStyles = ({
  hasAriaDisabledState,
  isLightColor,
  selected,
  isLoading,
  currentSelected,
  iconOnly
}: IButton): StyledCSS<IButton> => css<IButton>`
  transition:
    0.2s background-color ease,
    0.2s border-color ease;
  background-color: ${({ theme }) =>
    isLoading ? theme?.button?.secondary.loading?.backgroundColor : theme?.button?.secondary.backgroundColor};
  border-color: ${({ theme }) => theme?.button?.secondary.borderColor};
  color: ${isLightColor || isLoading ? COLOURS.WHITE : ({ theme }) => theme?.button?.secondary.color};

  border-radius: 4rem;

  &:hover {
    background-color: ${({ theme }) => theme?.button?.secondary.hover?.backgroundColor};
    border-color: ${({ theme }) => theme?.button?.secondary.hover?.borderColor};
    color: ${isLightColor || isLoading ? COLOURS.WHITE : ({ theme }) => theme?.button?.secondary.hover?.color};
  }

  &:active {
    background-color: ${({ theme }) => theme?.button?.secondary.active?.backgroundColor};
    border-color: ${({ theme }) => theme?.button?.secondary.active?.borderColor};
    color: ${isLightColor || isLoading ? COLOURS.WHITE : ({ theme }) => theme?.button?.secondary.active?.color};
  }

  ${isLoading &&
  css`
    cursor: not-allowed;
  `}

  ${isLightColor &&
  css`
    background-color: ${({ theme }) => theme?.button?.secondary.isLightColor?.backgroundColor};
    color: ${({ theme }) => theme?.button?.secondary.isLightColor?.color};
    border-color: ${({ theme }) => theme?.button?.secondary.isLightColor?.borderColor};

    &:hover {
      background-color: ${({ theme }) => theme?.button?.secondary.isLightColor?.hover?.backgroundColor};
      border-color: ${({ theme }) => theme?.button?.secondary.isLightColor?.hover?.borderColor};
      color: ${({ theme }) => theme?.button?.secondary.isLightColor?.hover?.color};
    }

    &:active {
      background-color: ${({ theme }) => theme?.button?.secondary.isLightColor?.active?.backgroundColor};
      color: ${({ theme }) => theme?.button?.secondary.isLightColor?.active?.color};
      border-color: ${({ theme }) => theme?.button?.secondary.isLightColor?.active?.borderColor};
    }
  `}

  ${SizeModifier};
  ${WidthModifier};
  ${hasAriaDisabledState &&
  css`
    &,
    &:hover {
      background-color: ${({ theme }) => theme?.button?.secondary.disabled.backgroundColor};
      border-color: ${({ theme }) => theme?.button?.secondary.disabled.borderColor};
      color: ${({ theme }) => {
        return theme?.button?.secondary.disabled.color;
      }};
    }
  `}

  ${selected &&
  css`
    cursor: default;
    background: ${({ theme }) => theme?.button?.secondary.selected?.background};
    border: ${({ theme }) => theme?.button?.secondary.selected?.border};
  `}

  ${currentSelected &&
  css`
    cursor: default;
    background: ${({ theme }) => theme?.button?.secondary.currentSelected?.background};
    border: ${({ theme }) => theme?.button?.secondary.currentSelected?.border};
  `}

  ${iconOnly &&
  css`
    ${iconOnlyButtonStyles}
  `}
`;

const TertiaryStyles = ({ hasAriaDisabledState, isLightColor, hasIconLeft }: IButton): StyledCSS<IButton> => {
  // The underlined "link" text lives in a different child span depending on
  // whether an icon sits to its left.
  const linkSelector = hasIconLeft ? 'span:last-child' : 'span:first-child';

  return css`
    padding: 1.2rem 0;
    border: none;
    color: ${({ theme }) => theme?.button?.tertiary.color};

    span > ${linkSelector} {
      color: ${({ theme }) => theme?.button?.tertiary.color};
      border-bottom: 0.2rem solid ${({ theme }) => theme?.button?.tertiary.borderColor};
      display: inline;
      line-height: 2.2rem;
    }

    &:hover,
    &:active {
      > span > ${linkSelector} {
        color: ${({ theme }) => theme?.button?.tertiary.hover?.color};
        border-bottom-color: ${({ theme }) => theme?.button?.tertiary.hover?.borderColor};
      }
    }

    &:focus > span > ${linkSelector} {
      color: ${({ theme }) => theme?.button?.tertiary.focus?.color};
      border-bottom-color: ${({ theme }) => theme?.button?.tertiary.focus?.borderColor};
    }

    ${isLightColor &&
    css`
      color: ${COLOURS.WHITE};

      span > ${linkSelector} {
        color: ${COLOURS.WHITE};
        border-bottom-color: ${({ theme }) => theme?.button?.tertiary.isLightColor?.borderColor};
      }

      &:hover > span > ${linkSelector} {
        color: ${COLOURS.WHITE};
        border-bottom-color: ${({ theme }) => theme?.button?.tertiary.isLightColor?.hover?.borderColor};
      }

      &:active,
      &:focus {
        > span > ${linkSelector} {
          color: ${COLOURS.WHITE};
          border-bottom-color: ${COLOURS.BELONG_BLUE};
        }
      }
    `}

    ${hasAriaDisabledState &&
    css`
      &,
      &:hover,
      &:active,
      &:focus {
        > span > ${linkSelector} {
          color: ${({ theme }) => theme?.button?.tertiary.disabled.color};
          border-bottom-color: ${({ theme }) => theme?.button?.tertiary.disabled.borderColor};
        }
      }
    `}
  `;
};

const QuaternaryStyles = ({
  hasAriaDisabledState,
  isFootnoteLink,
  isLightColor,
  isSmallFontSize
}: IButton): StyledCSS<IButton> => css`
  padding: 0;
  border: none;
  text-transform: none;
  text-decoration: underline;
  color: ${isLightColor ? COLOURS.BELONG_BLUE_LIGHT : ({ theme }) => theme?.button?.quaternary.color};
  font-size: inherit;
  letter-spacing: 0;
  text-align: inherit;
  font-weight: ${FONT_WEIGHT.SEMI_BOLD};

  ${isFootnoteLink &&
  css`
    /* Simulate the "sup" tag */
    font-size: 75%;
    position: relative;
    vertical-align: baseline;
    /* This is intensionally em, not rem */
    top: -0.5em;
  `}

  ${isSmallFontSize &&
  css`
    ${getBodyFont('medium')}
  `}

  &:hover {
    text-decoration: none;
    color: ${isLightColor ? COLOURS.WHITE : ({ theme }) => theme?.button?.quaternary?.focus?.color};
  }

  &:focus,
  &:active {
    color: ${isLightColor ? COLOURS.WHITE : ({ theme }) => theme?.button?.quaternary?.focus?.color};
    text-decoration: 'underline';
  }

  ${hasAriaDisabledState &&
  css`
    &,
    &:hover,
    &:focus,
    &:active {
      text-decoration: underline;
      color: ${({ theme }) => theme?.button?.quaternary.disabled.color};
      outline: none;
      box-shadow: none;
    }
  `}

  && > * {
    color: inherit;
  }
`;

export const getVariantStyles = (props: IButton): StyledCSS<any> => {
  switch (props.variant) {
    case 'secondary':
      return SecondaryStyles(props);
    case 'tertiary':
      return TertiaryStyles(props);
    case 'quaternary':
      return QuaternaryStyles(props);
    case 'primary':
    default:
      return PrimaryStyles(props);
  }
};

export const StyledIcon = styled(Icon)<{ variant?: ButtonVariantValue; hasIconLeft?: boolean; iconOnly?: boolean }>`
  ${({ hasIconLeft }) => css`
    margin-right: ${hasIconLeft ? '0.8rem' : '0'};
    margin-left: ${hasIconLeft ? '0' : '0.8rem'};
  `}

  ${({ variant }) =>
    variant === BUTTON_VARIANTS.PRIMARY &&
    css`
      margin-bottom: 0;
    `}

  ${({ variant }) =>
    variant === BUTTON_VARIANTS.QUATERNARY &&
    mediaMap(
      { xs: '0.2rem', md: '0' },
      (marginRight): StyledCSS => css`
        margin-right: ${marginRight};
      `
    )}

  ${({ iconOnly }) =>
    iconOnly &&
    css`
      ${iconOnlyIconStyles}
    `}
`;

export const SelectedIcon = styled(Icon)``;

const Button = styled(ButtonUnstyled)<IButton>`
  ${BaseStyles};
  ${BaseFontStyles};

  ${getVariantStyles};

  ${({ isLoading }) =>
    isLoading &&
    css`
      overflow-x: hidden;
    `}
`;

export const FocusRing = styled.span<Partial<IButton>>`
  display: none;

  ${Button}:not([disabled]):not([aria-disabled='true']):focus &&,
  ${Button}:not([disabled]):not([aria-disabled='true']):active && {
    position: absolute;
    display: block;
    width: calc(100% + (${focusRingOffset} * 2));
    height: calc(100% + (${focusRingOffset}) * 2);
    top: -${focusRingOffset};
    left: -${focusRingOffset};
    border: 0.2rem solid ${({ theme }) => theme.focusRingColor};
    border-radius: ${BORDER_RADIUS.BUTTON};

    // tertiary / quat buttons have no natural border so we have to make one
    ${({ variant }) =>
      [BUTTON_VARIANTS.TERTIARY, BUTTON_VARIANTS.QUATERNARY].includes(
        variant as ObjectValues<typeof BUTTON_VARIANTS>
      ) &&
      css`
        width: calc(100% + (${focusRingOffset} * 5));
        left: calc(-${focusRingOffset} * 2.5);
        height: calc(100%);
        top: 0;
      `}

    // quat button focus ring must be bigger than the text itself
    ${({ variant }) =>
      variant === BUTTON_VARIANTS.QUATERNARY &&
      css`
        padding-top: ${focusRingOffset};
        padding-bottom: ${focusRingOffset};
        box-sizing: content-box; // to ensure the padding is added to the height
        top: calc(-${focusRingOffset} * 1.5); // visually adjust upwards
        width: calc(100% + (${focusRingOffset} * 4));
      `}
  }
`;
FocusRing.displayName = 'FocusRing';

export default Button;
